import { provideAppInitializer, inject } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { filter, switchMap, take, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { FeatureFlagService } from './feature-flag.service';

/**
 * Registers an app initializer that loads feature flags for the given portal key
 * after Auth0 authentication resolves.
 *
 * Usage in main.ts:
 *   provideFeatureFlags(environment.auth.portalKey)
 */
export function provideFeatureFlags(portalKey: string) {
  return provideAppInitializer(() => {
    const auth = inject(AuthService);
    const featureFlagService = inject(FeatureFlagService);

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    // /callback has no access token yet; CallbackComponent loads flags after Auth0.
    if (path.includes('/callback')) {
      return Promise.resolve();
    }

    return firstValueFrom(
      auth.isLoading$.pipe(
        filter(loading => !loading),
        take(1),
        switchMap(() => auth.isAuthenticated$.pipe(take(1))),
        switchMap(isAuthenticated => {
          if (!isAuthenticated) return of(void 0);
          return featureFlagService.load(portalKey).pipe(
            catchError(err => {
              console.warn('[provideFeatureFlags] Feature flag load failed:', err);
              return of(void 0);
            })
          );
        })
      )
    );
  });
}
